import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Nav, NavItem, NavLink, TabContent, TabPane, Button, Spinner, Alert } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { getAppConfiguration } from '../../slices/appSettings/thunk';
import classnames from "classnames";
import { APIClient } from "../../helpers/api_helper";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BreadCrumb from '../../Components/Common/BreadCrumb';

const Settings = () => {
    document.title = "App Settings";
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState("1");
    const [formData, setFormData] = useState(null);
    const [isEdited, setIsEdited] = useState(false);
    const [originalData, setOriginalData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    const { appConfig, loading, error } = useSelector((state) => ({
        appConfig: state.AppSettings?.appConfig?.data || null,
        loading: state.AppSettings?.loading || false,
        error: state.AppSettings?.error || null
    }));

    useEffect(() => {
        dispatch(getAppConfiguration());
    }, [dispatch]);

    useEffect(() => {
        if (appConfig) {
            const mappedData = {
                ...appConfig,
                FREE_SUBSCRIPTION_PLAN: {
                    ...appConfig.FREE_SUBSCRIPTION_PLAN,
                    dailyLikeCount: {
                        ...appConfig.FREE_SUBSCRIPTION_PLAN.dailyLikeCount,
                        description: appConfig.FREE_SUBSCRIPTION_PLAN.dailyLikeCount?.description || {
                            en: ''
                        }
                    },
                    noOfTokensIncluded: {
                        ...appConfig.FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded,
                        description: appConfig.FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded?.description || {
                            en: ''
                        }
                    },
                    myList: {
                        ...appConfig.FREE_SUBSCRIPTION_PLAN.myList,
                        maybeValueDescription: appConfig.FREE_SUBSCRIPTION_PLAN.myList.maybeValueDescription || {
                            en: ''
                        },
                        myLikesDescription: appConfig.FREE_SUBSCRIPTION_PLAN.myList.myLikesDescription || {
                            en: ''
                        },
                        likedHooksValueDescription: appConfig.FREE_SUBSCRIPTION_PLAN.myList.likedHooksValueDescription || {
                            en: ''
                        }
                    }
                },
                copyrightText: appConfig.COPYRIGHT_TEXT,
                supportMail: appConfig.SUPPORT_MAIL,
                tokenForNewUserWithoutReferralCode: appConfig.TOKEN_FOR_NEW_USER_WITHOUT_REFERRAL_CODE,
                tokenForNewUserWithReferralCode: appConfig.TOKEN_FOR_NEW_USER_WITH_REFERRAL_CODE,
                tokenToCreditWhoseReferralCode: appConfig.TOKEN_TO_CREDIT_WHOSE_REFERRAL_CODE,
                maxRadiusInMiles: appConfig.MAX_RADIUS_IN_MILES,
                maxRadiusInKilometers: appConfig.MAX_RADIUS_IN_KILOMETERS,
                maxAgeInYear: appConfig.MAX_AGE_IN_YEAR,
                bufferTimeForschedulingDateInSeconds: appConfig.BUFFER_TIME_FOR_SCHEDULING_DATE_IN_SECONDS,
                durationOfInPersonDateInSeconds: appConfig.DURATION_OF_IN_PERSON_DATE_IN_SECONDS,
                durationOfOnlineDateInSeconds: appConfig.DURATION_OF_ONLINE_DATE_IN_SECONDS,
                firstReminderOfUpcomingDateInSeconds: appConfig.FIRST_REMINDER_OF_UPCOMING_DATE_IN_SECONDS,
                secondReminderOfUpcomingDateInSeconds: appConfig.SECOND_REMINDER_OF_UPCOMING_DATE_IN_SECONDS,
                coolingOfPeriodForPassedUser: appConfig.COOLING_OF_PERIOD_FOR_PASSED_USER,
                freeMinForVideoCallMatch: appConfig.FREE_MIN_FOR_VIDEO_CALL_MATCH
            };
            
            setFormData(mappedData);
            setOriginalData(mappedData);
        }
    }, [appConfig]);

    const tabChange = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const handleInputChange = (field, value) => {
        setIsEdited(true);
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const mapVisibilityToBoolean = (value) => {
        return value === 'Visible';
    };

    const handleToggle = (section) => {
        setIsEdited(true);
        if (section === 'adsVisibility') {
            setFormData(prev => ({
                ...prev,
                FREE_SUBSCRIPTION_PLAN: {
                    ...prev.FREE_SUBSCRIPTION_PLAN,
                    adsVisibility: {
                        ...prev.FREE_SUBSCRIPTION_PLAN.adsVisibility,
                        value: prev.FREE_SUBSCRIPTION_PLAN.adsVisibility.value === 'Visible' ? 'Hide' : 'Visible'
                    }
                }
            }));
        } else if (section === 'allowUnlimitedRewinds' || section === 'passportToAnotherLocation' || section === 'aiMatches') {
            // Handle Enabled/Disabled toggles
            setFormData(prev => ({
                ...prev,
                FREE_SUBSCRIPTION_PLAN: {
                    ...prev.FREE_SUBSCRIPTION_PLAN,
                    [section]: {
                        ...prev.FREE_SUBSCRIPTION_PLAN[section],
                        value: prev.FREE_SUBSCRIPTION_PLAN[section].value === 'Enabled' ? 'Disabled' : 'Enabled'
                    }
                }
            }));
        } else if (section === 'advanceFilterAccess' || section === 'controlWhoSeesYourProfile') {
            // Handle boolean toggles
            setFormData(prev => ({
                ...prev,
                FREE_SUBSCRIPTION_PLAN: {
                    ...prev.FREE_SUBSCRIPTION_PLAN,
                    [section]: {
                        ...prev.FREE_SUBSCRIPTION_PLAN[section],
                        value: !prev.FREE_SUBSCRIPTION_PLAN[section].value  // Toggle boolean value
                    }
                }
            }));
        } else if (section === 'recentVisitors' || section === 'myMatches' || section === 'maybe' || section === 'myLikes' || section === 'likesMe' || section === 'likedHooks') {
            // Handle My List toggles
            const valueKey = `${section}Value`;
            setFormData(prev => ({
                ...prev,
                FREE_SUBSCRIPTION_PLAN: {
                    ...prev.FREE_SUBSCRIPTION_PLAN,
                    myList: {
                        ...prev.FREE_SUBSCRIPTION_PLAN.myList,
                        [valueKey]: !prev.FREE_SUBSCRIPTION_PLAN.myList[valueKey]
                    }
                }
            }));
        }
    };

    const handleMyListToggle = (field) => {
        setIsEdited(true);
        setFormData(prev => ({
            ...prev,
            FREE_SUBSCRIPTION_PLAN: {
                ...prev.FREE_SUBSCRIPTION_PLAN,
                myList: {
                    ...prev.FREE_SUBSCRIPTION_PLAN.myList,
                    [field]: !prev.FREE_SUBSCRIPTION_PLAN.myList[field]
                }
            }
        }));
    };

    const handleDescriptionChange = (section, field, value) => {
        setIsEdited(true);
        setFormData(prev => ({
            ...prev,
            FREE_SUBSCRIPTION_PLAN: {
                ...prev.FREE_SUBSCRIPTION_PLAN,
                [section]: {
                    ...prev.FREE_SUBSCRIPTION_PLAN[section],
                    description: {
                        en: value
                    }
                }
            }
        }));
    };

    const handleMyListDescriptionChange = (field, value) => {
        setIsEdited(true);
        setFormData(prev => ({
            ...prev,
            FREE_SUBSCRIPTION_PLAN: {
                ...prev.FREE_SUBSCRIPTION_PLAN,
                myList: {
                    ...prev.FREE_SUBSCRIPTION_PLAN.myList,
                    [`${field}${field === 'maybe' ? 'ValueDescription' : field === 'myLikes' ? 'Description' : field === 'likedHooks' ? 'ValueDescription' : 'Description'}`]: {
                        en: value
                    }
                }
            }
        }));
    };

    const hasValueChanged = (currentValue, originalValue) => {
        return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
    };

    const buildPatchPayload = useCallback(() => {
        const payload = {};

        // Helper function to add changed value to payload
        const addIfChanged = (key, currentValue, originalValue, isNumeric = false) => {
            if (hasValueChanged(currentValue, originalValue)) {
                payload[key] = isNumeric ? Number(currentValue) : currentValue;
            }
        };

        // Basic fields mapping with type information
        const mappings = {
            copyrightText: { key: 'COPYRIGHT_TEXT', isNumeric: false },
            supportMail: { key: 'SUPPORT_MAIL', isNumeric: false },
            retryAllowAttempt: { key: 'RETRY_ALLOW_ATTEMPT', isNumeric: true },
            maxAttemptVerification: { key: 'MAX_ATTEMPT_VERIFICATION', isNumeric: true },
            expireOtp: { key: 'EXPIRE_OTP', isNumeric: true },
            expireEmailTime: { key: 'EXPIRE_EMAIL_TIME', isNumeric: true },
            faceVerificationToken: { key: 'FACE_VERIFICATION_TOKEN', isNumeric: false },
            aiModelApiKey: { key: 'AI_MODEL_API_KEY', isNumeric: false },
            aiIceBreakUrl: { key: 'AI_ICE_BREAK_URL', isNumeric: false },
            aiRecommendationUrl: { key: 'AI_RECOMMENDATION_URL', isNumeric: false },
            aiStarUrl: { key: 'AI_STAR_URL', isNumeric: false },
            tokenForNewUserWithoutReferralCode: { key: 'TOKEN_FOR_NEW_USER_WITHOUT_REFERRAL_CODE', isNumeric: true },
            tokenForNewUserWithReferralCode: { key: 'TOKEN_FOR_NEW_USER_WITH_REFERRAL_CODE', isNumeric: true },
            tokenToCreditWhoseReferralCode: { key: 'TOKEN_TO_CREDIT_WHOSE_REFERRAL_CODE', isNumeric: true },
            maxRadiusInMiles: { key: 'MAX_RADIUS_IN_MILES', isNumeric: true },
            maxRadiusInKilometers: { key: 'MAX_RADIUS_IN_KILOMETERS', isNumeric: true },
            maxAgeInYear: { key: 'MAX_AGE_IN_YEAR', isNumeric: true },
            matchLimitPerDayForFreeUser: { key: 'MATCH_LIMIT_PER_DAY_FOR_FREE_USER', isNumeric: true },
            tokenForSuperLike: { key: 'TOKEN_FOR_SUPER_LIKE', isNumeric: true },
            noOfCoinForPaidChatMsg: { key: 'NO_OF_COIN_FOR_PAID_CHAT_MSG', isNumeric: true },
            noOfCoinForIceBreaker: { key: 'NO_OF_COIN_FOR_ICE_BREAKER', isNumeric: true },
            bufferTimeForschedulingDateInSeconds: { key: 'BUFFER_TIME_FOR_SCHEDULING_DATE_IN_SECONDS', isNumeric: true },
            durationOfInPersonDateInSeconds: { key: 'DURATION_OF_IN_PERSON_DATE_IN_SECONDS', isNumeric: true },
            durationOfOnlineDateInSeconds: { key: 'DURATION_OF_ONLINE_DATE_IN_SECONDS', isNumeric: true },
            firstReminderOfUpcomingDateInSeconds: { key: 'FIRST_REMINDER_OF_UPCOMING_DATE_IN_SECONDS', isNumeric: true },
            secondReminderOfUpcomingDateInSeconds: { key: 'SECOND_REMINDER_OF_UPCOMING_DATE_IN_SECONDS', isNumeric: true },
            enableRewardOnAds: { key: 'ENABLE_REWARD_ON_ADS', isNumeric: false },
            tokenCostForChatBeforeMatch: { key: 'TOKEN_COST_FOR_CHAT_BEFORE_MATCH', isNumeric: true },
            noChatMsgWithoutMatch: { key: 'NO_CHAT_MSG_WITHOUT_MATCH', isNumeric: true },
            rewardOnAds: { key: 'REWARD_ON_ADS', isNumeric: false },
            coolingOfPeriodForPassedUser: { key: 'COOLING_OF_PERIOD_FOR_PASSED_USER', isNumeric: true },
            freeMinForVideoCallMatch: { key: 'FREE_MIN_FOR_VIDEO_CALL_MATCH', isNumeric: true }
        };

        Object.entries(mappings).forEach(([payloadKey, { key, isNumeric }]) => {
            addIfChanged(payloadKey, formData[key], originalData[key], isNumeric);
        });

        // Company Details
        if (hasValueChanged(formData.COMPANY_DETAIL, originalData.COMPANY_DETAIL)) {
            payload.companyDetail = {
                companyName: formData.COMPANY_DETAIL.companyName,
                address: formData.COMPANY_DETAIL.address,
                city: formData.COMPANY_DETAIL.city,
                country: formData.COMPANY_DETAIL.country,
                zipCode: formData.COMPANY_DETAIL.zipCode,
                taxId: formData.COMPANY_DETAIL.taxId
            };
        }

        // Boost Profile
        if (hasValueChanged(formData.BOOST_PROFILE, originalData.BOOST_PROFILE)) {
            payload.boostProfile = {
                minutes: Number(formData.BOOST_PROFILE.minutes),
                cost: Number(formData.BOOST_PROFILE.cost),
                radius: formData.BOOST_PROFILE.radius.toString() + ".00",
                radiusUnit: formData.BOOST_PROFILE.radiusUnit
            };
        }

        // Extension Plan for Video Call
        if (hasValueChanged(formData.EXTENSION_PLAN_FOR_VIDEO_CALL, originalData.EXTENSION_PLAN_FOR_VIDEO_CALL)) {
            payload.extensionPlanForVideoCall = formData.EXTENSION_PLAN_FOR_VIDEO_CALL.map(plan => ({
                min: Number(plan.min),
                tokens: Number(plan.tokens)
            }));
        }

        // Free Subscription Plan
        if (hasValueChanged(formData.FREE_SUBSCRIPTION_PLAN, originalData.FREE_SUBSCRIPTION_PLAN)) {
            payload.freeSubscriptionPlan = {
                adsVisibility: {
                    value: formData.FREE_SUBSCRIPTION_PLAN.adsVisibility.value,
                    description: formData.FREE_SUBSCRIPTION_PLAN.adsVisibility.description
                },
                noOfTokensIncluded: {
                    value: Number(formData.FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded.value),
                    description: formData.FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded.description
                },
                myList: {
                    recentVisitorsValue: formData.FREE_SUBSCRIPTION_PLAN.myList.recentVisitorsValue,
                    recentVisitorsDescription: formData.FREE_SUBSCRIPTION_PLAN.myList.recentVisitorsDescription,
                    myMatchesValue: formData.FREE_SUBSCRIPTION_PLAN.myList.myMatchesValue,
                    myMatchesDescription: formData.FREE_SUBSCRIPTION_PLAN.myList.myMatchesDescription,
                    maybeValue: formData.FREE_SUBSCRIPTION_PLAN.myList.maybeValue,
                    maybeValueDescription: formData.FREE_SUBSCRIPTION_PLAN.myList.maybeValueDescription,
                    myLikesValue: formData.FREE_SUBSCRIPTION_PLAN.myList.myLikesValue,
                    myLikesDescription: formData.FREE_SUBSCRIPTION_PLAN.myList.myLikesDescription,
                    likesMeValue: formData.FREE_SUBSCRIPTION_PLAN.myList.likesMeValue,
                    likesMeDescription: formData.FREE_SUBSCRIPTION_PLAN.myList.likesMeDescription,
                    likedHooksValue: formData.FREE_SUBSCRIPTION_PLAN.myList.likedHooksValue,
                    likedHooksValueDescription: formData.FREE_SUBSCRIPTION_PLAN.myList.likedHooksValueDescription
                },
                allowUnlimitedRewinds: formData.FREE_SUBSCRIPTION_PLAN.allowUnlimitedRewinds,
                passportToAnotherLocation: formData.FREE_SUBSCRIPTION_PLAN.passportToAnotherLocation,
                advanceFilterAccess: formData.FREE_SUBSCRIPTION_PLAN.advanceFilterAccess,
                controlWhoSeesYourProfile: formData.FREE_SUBSCRIPTION_PLAN.controlWhoSeesYourProfile,
                aiMatches: {
                    ...formData.FREE_SUBSCRIPTION_PLAN.aiMatches,
                    value: formData.FREE_SUBSCRIPTION_PLAN.aiMatches.value === 'Enabled'  // Convert to boolean
                },
                dailyLikeCount: formData.FREE_SUBSCRIPTION_PLAN.dailyLikeCount
            };
        }

        // Update Reward on Ads mapping
        if (hasValueChanged(formData.REWARD_ON_ADS, originalData.REWARD_ON_ADS)) {
            payload.rewardOnAds = {
                rewadedAdsCountToEarnCoins: Number(formData.REWARD_ON_ADS.rewadedAdsCountToEarnCoins),
                numberOfCoinsAsReward: Number(formData.REWARD_ON_ADS.numberOfCoinsAsReward)
            };
        }

        return payload;
    }, [formData, originalData]);

    const validateForm = () => {
        const errors = [];

        // Basic validation rules
        if (formData.NO_OF_COIN_FOR_ICE_BREAKER < 0) {
            errors.push("Ice Breaker coins cannot be negative");
        }
        if (formData.NO_OF_COIN_FOR_PAID_CHAT_MSG < 0) {
            errors.push("Paid chat message coins cannot be negative");
        }
        if (formData.TOKEN_FOR_SUPER_LIKE < 0) {
            errors.push("Super like tokens cannot be negative");
        }

        // Validate video call extension plans
        if (formData.EXTENSION_PLAN_FOR_VIDEO_CALL.some(plan => plan.min < 0 || plan.tokens < 0)) {
            errors.push("Video call extension plan values cannot be negative");
        }

        // Validate boost profile
        if (formData.BOOST_PROFILE.minutes < 0 || formData.BOOST_PROFILE.cost < 0 || formData.BOOST_PROFILE.radius < 0) {
            errors.push("Boost profile values cannot be negative");
        }

        // Validate reward on ads
        if (formData.ENABLE_REWARD_ON_ADS) {
            if (formData.REWARD_ON_ADS.rewadedAdsCountToEarnCoins < 0 || 
                formData.REWARD_ON_ADS.numberOfCoinsAsReward < 0) {
                errors.push("Reward on ads values cannot be negative");
            }
        }

        // Company details validation
        if (formData.COMPANY_DETAIL.zipCode && !/^\d{5,6}$/.test(formData.COMPANY_DETAIL.zipCode)) {
            errors.push("Invalid zip code format");
        }

        return errors;
    };

    const hideAlert = () => {
        setTimeout(() => {
            setAlert(prev => {
                if (prev.show) {
                    return { show: false, message: '', type: '' };
                }
                return prev;
            });
        }, 5000);
    };

    const handleSave = async () => {
        try {
            // Validate form
            const validationErrors = validateForm();
            if (validationErrors.length > 0) {
                validationErrors.forEach(error => {
                    toast.error(error, {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        theme: "colored"
                    });
                });
                return;
            }

            const payload = buildPatchPayload();
            
            if (Object.keys(payload).length === 0) {
                toast.info("No changes to save", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
                return;
            }

            setIsSaving(true);
            
            const response = await APIClient.patch('/appConfig', payload);
            
            if (response && response.data && response.data.message === 'Success') {
                setOriginalData(formData);
                setIsEdited(false);
                
                toast.success("Settings updated successfully!", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored",
                    toastId: "success-toast"
                });

                // Refresh the configuration
                dispatch(getAppConfiguration());
            } else {
                throw new Error(response?.data?.message || 'Update failed');
            }
        } catch (error) {
            console.error('Error updating configuration:', error);
            toast.error(error.message || "Error saving settings. Please try again.", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setIsSaving(false);
        }
    };

    const SaveButton = () => (
        <div className="text-end mt-3">
            <Button 
                color="primary" 
                onClick={handleSave}
                disabled={isSaving}
            >
                {isSaving ? (
                    <>
                        <Spinner size="sm" className="me-2">
                            Loading...
                        </Spinner>
                        Saving...
                    </>
                ) : (
                    'Save Changes'
                )}
            </Button>
        </div>
    );

    useEffect(() => {
        const handleError = (error) => {
            console.error('Unhandled error:', error);
            toast.error("An unexpected error occurred. Please refresh the page.", {
                position: "top-right",
                autoClose: false,
                closeOnClick: false
            });
        };

        window.addEventListener('error', handleError);
        return () => window.removeEventListener('error', handleError);
    }, []);

    // First, add the favouriteBtn function
    const favouriteBtn = (target) => {
        target.closest("button").classList.toggle("active");
    };

    if (!formData) return null;

    return (
        <div className="page-content">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            <Container fluid>
                <BreadCrumb title="App Settings" pageTitle="Settings" />
                
                <Row>
                    <Col xxl={12}>
                        <Card>
                            <CardHeader>
                                <Nav className="nav-tabs-custom rounded card-header-tabs border-bottom-0" role="tablist">
                                    <NavItem>
                                        <NavLink to="#" type="button"
                                            className={classnames({ active: activeTab === "1" })}
                                            onClick={() => { tabChange("1"); }}>
                                            <i className="fas fa-home"></i>
                                            Basic Settings
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink to="#" type="button"
                                            className={classnames({ active: activeTab === "2" })}
                                            onClick={() => { tabChange("2"); }}>
                                            <i className="far fa-building"></i>
                                            Company Details
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink to="#" type="button"
                                            className={classnames({ active: activeTab === "3" })}
                                            onClick={() => { tabChange("3"); }}>
                                            <i className="far fa-chart-bar"></i>
                                            Boost Settings
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink to="#" type="button"
                                            className={classnames({ active: activeTab === "4" })}
                                            onClick={() => { tabChange("4"); }}>
                                            <i className="fas fa-star"></i>
                                            Free Subscription
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink to="#" type="button"
                                            className={classnames({ active: activeTab === "5" })}
                                            onClick={() => { tabChange("5"); }}>
                                            <i className="fas fa-comments"></i>
                                            Chat and Call
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink to="#" type="button"
                                            className={classnames({ active: activeTab === "6" })}
                                            onClick={() => { tabChange("6"); }}>
                                            <i className="fas fa-robot"></i>
                                            AI Settings
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </CardHeader>
                            <CardBody className="p-4">
                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId="1">
                                        {/* Authentication Settings */}
                                        <Card className="border">
                                            <CardHeader>
                                                <h4 className="card-title mb-0">Authentication Settings</h4>
                                            </CardHeader>
                                            <CardBody>
                                                <Row className="mb-3">
                                                    <Col lg={3}>
                                                        <div>
                                                            <Label htmlFor="maxAttempt" className="form-label">Max Verification Attempts</Label>
                                                            <Input 
                                                                type="number" 
                                                                className="form-control" 
                                                                name="MAX_ATTEMPT_VERIFICATION"
                                                                value={formData.MAX_ATTEMPT_VERIFICATION} 
                                                                onChange={(e) => handleInputChange('MAX_ATTEMPT_VERIFICATION', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={3}>
                                                        <div>
                                                            <Label htmlFor="retryAttempt" className="form-label">Retry Allow Attempts</Label>
                                                            <Input 
                                                                type="number" 
                                                                className="form-control" 
                                                                name="RETRY_ALLOW_ATTEMPT"
                                                                value={formData.RETRY_ALLOW_ATTEMPT} 
                                                                onChange={(e) => handleInputChange('RETRY_ALLOW_ATTEMPT', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={3}>
                                                        <div>
                                                            <Label htmlFor="expireOtp" className="form-label">OTP Expiry (minutes)</Label>
                                                            <Input 
                                                                type="number" 
                                                                className="form-control" 
                                                                name="EXPIRE_OTP"
                                                                value={formData.EXPIRE_OTP} 
                                                                onChange={(e) => handleInputChange('EXPIRE_OTP', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={3}>
                                                        <div>
                                                            <Label htmlFor="expireEmail" className="form-label">Email Expiry (minutes)</Label>
                                                            <Input 
                                                                type="number" 
                                                                className="form-control" 
                                                                name="EXPIRE_EMAIL_TIME"
                                                                value={formData.EXPIRE_EMAIL_TIME} 
                                                                onChange={(e) => handleInputChange('EXPIRE_EMAIL_TIME', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>

                                        {/* Token Settings */}
                                        <Card className="border">
                                            <CardHeader>
                                                <h4 className="card-title mb-0">Token Settings</h4>
                                            </CardHeader>
                                            <CardBody>
                                                <Row className="mb-3">
                                                    <Col lg={4}>
                                                        <div>
                                                            <Label htmlFor="chatToken" className="form-label">Token Cost for Chat Before Match</Label>
                                                            <Input 
                                                                type="number" 
                                                                className="form-control" 
                                                                name="TOKEN_COST_FOR_CHAT_BEFORE_MATCH"
                                                                value={formData.TOKEN_COST_FOR_CHAT_BEFORE_MATCH} 
                                                                onChange={(e) => handleInputChange('TOKEN_COST_FOR_CHAT_BEFORE_MATCH', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div>
                                                            <Label htmlFor="noChatMsg" className="form-label">No Chat Messages Without Match</Label>
                                                            <Input 
                                                                type="number" 
                                                                className="form-control" 
                                                                name="NO_CHAT_MSG_WITHOUT_MATCH"
                                                                value={formData.NO_CHAT_MSG_WITHOUT_MATCH} 
                                                                onChange={(e) => handleInputChange('NO_CHAT_MSG_WITHOUT_MATCH', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div>
                                                            <Label htmlFor="matchLimit" className="form-label">Daily Match Limit (Free Users)</Label>
                                                            <Input 
                                                                type="number" 
                                                                className="form-control" 
                                                                name="MATCH_LIMIT_PER_DAY_FOR_FREE_USER"
                                                                value={formData.MATCH_LIMIT_PER_DAY_FOR_FREE_USER} 
                                                                onChange={(e) => handleInputChange('MATCH_LIMIT_PER_DAY_FOR_FREE_USER', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>

                                        {/* Reward Settings */}
                                        <Card className="border mb-4">
                                            <CardHeader>
                                                <h5 className="card-title mb-0">Reward Settings</h5>
                                            </CardHeader>
                                            <CardBody>
                                                <Row>
                                                    <Col lg={12}>
                                                        <div className="mb-3">
                                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                                <Label className="form-label mb-0">Enable Reward on Ads</Label>
                                                                {console.log('ENABLE_REWARD_ON_ADS value:', formData.ENABLE_REWARD_ON_ADS)}
                                                                <div className="d-flex flex-wrap align-items-center">
                                                                    <Button 
                                                                        color='success' 
                                                                        className={`custom-toggle ${!formData.ENABLE_REWARD_ON_ADS ? 'active' : ''}`}
                                                                        onClick={(e) => {
                                                                            favouriteBtn(e.target);
                                                                            handleInputChange('ENABLE_REWARD_ON_ADS', !formData.ENABLE_REWARD_ON_ADS);
                                                                        }}
                                                                    >
                                                                        <span className="icon-off">
                                                                            <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                        </span>
                                                                        <span className="icon-on">
                                                                            <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                        </span>
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    {formData.ENABLE_REWARD_ON_ADS && (
                                                        <>
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Required Ads Count</Label>
                                                                    <Input 
                                                                        type="number"
                                                                        className="form-control"
                                                                        value={formData.REWARD_ON_ADS.rewadedAdsCountToEarnCoins}
                                                                        onChange={(e) => handleInputChange('REWARD_ON_ADS', { ...formData.REWARD_ON_ADS, rewadedAdsCountToEarnCoins: parseInt(e.target.value) })}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Coins as Reward</Label>
                                                                    <Input 
                                                                        type="number"
                                                                        className="form-control"
                                                                        value={formData.REWARD_ON_ADS.numberOfCoinsAsReward}
                                                                        onChange={(e) => handleInputChange('REWARD_ON_ADS', { ...formData.REWARD_ON_ADS, numberOfCoinsAsReward: parseInt(e.target.value) })}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </>
                                                    )}
                                                </Row>
                                            </CardBody>
                                        </Card>

                                        {/* URL Configuration */}
                                        <Card className="border">
                                            <CardHeader>
                                                <h5 className="card-title mb-0">URL Configuration</h5>
                                            </CardHeader>
                                            <CardBody>
                                                <Row>
                                                    <Col lg={12}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Image Base URL</Label>
                                                            <div className="d-flex gap-2">
                                                                <Input 
                                                                    type="text"
                                                                    className="form-control font-monospace bg-light"
                                                                    value={formData.imageBaseUrl}
                                                                    readOnly
                                                                />
                                                                <Button 
                                                                    color="light" 
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(formData.imageBaseUrl);
                                                                    }}
                                                                    title="Copy to clipboard"
                                                                >
                                                                    <i className="ri-file-copy-line"></i>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg={12}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Base URL</Label>
                                                            <div className="d-flex gap-2">
                                                                <Input 
                                                                    type="text"
                                                                    className="form-control font-monospace bg-light"
                                                                    value={formData.baseUrl}
                                                                    readOnly
                                                                />
                                                                <Button 
                                                                    color="light" 
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(formData.baseUrl);
                                                                    }}
                                                                    title="Copy to clipboard"
                                                                >
                                                                    <i className="ri-file-copy-line"></i>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg={12}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Non Transform URL</Label>
                                                            <div className="d-flex gap-2">
                                                                <Input 
                                                                    type="text"
                                                                    className="form-control font-monospace bg-light"
                                                                    value={formData.nonTransformUrl}
                                                                    readOnly
                                                                />
                                                                <Button 
                                                                    color="light" 
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(formData.nonTransformUrl);
                                                                    }}
                                                                    title="Copy to clipboard"
                                                                >
                                                                    <i className="ri-file-copy-line"></i>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>

                                        {/* Add new section below URL Configuration */}
                                        <Card className="mt-4">
                                            <CardHeader>
                                                <h5 className="card-title mb-0">Additional Settings</h5>
                                            </CardHeader>
                                            <CardBody>
                                                <Row>
                                                    {/* Token Settings */}
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Tokens for New User (Without Referral)</Label>
                                                            <Input
                                                                type="number"
                                                                name="TOKEN_FOR_NEW_USER_WITHOUT_REFERRAL_CODE"
                                                                value={formData.TOKEN_FOR_NEW_USER_WITHOUT_REFERRAL_CODE || ''}
                                                                onChange={(e) => handleInputChange('TOKEN_FOR_NEW_USER_WITHOUT_REFERRAL_CODE', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Tokens for New User (With Referral)</Label>
                                                            <Input
                                                                type="number"
                                                                name="TOKEN_FOR_NEW_USER_WITH_REFERRAL_CODE"
                                                                value={formData.TOKEN_FOR_NEW_USER_WITH_REFERRAL_CODE || ''}
                                                                onChange={(e) => handleInputChange('TOKEN_FOR_NEW_USER_WITH_REFERRAL_CODE', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Tokens for Referral Code Owner</Label>
                                                            <Input
                                                                type="number"
                                                                name="TOKEN_TO_CREDIT_WHOSE_REFERRAL_CODE"
                                                                value={formData.TOKEN_TO_CREDIT_WHOSE_REFERRAL_CODE || ''}
                                                                onChange={(e) => handleInputChange('TOKEN_TO_CREDIT_WHOSE_REFERRAL_CODE', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>

                                                    {/* Distance and Age Settings */}
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Max Radius (Miles)</Label>
                                                            <Input
                                                                type="number"
                                                                name="MAX_RADIUS_IN_MILES"
                                                                value={formData.MAX_RADIUS_IN_MILES || ''}
                                                                onChange={(e) => handleInputChange('MAX_RADIUS_IN_MILES', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Max Radius (Kilometers)</Label>
                                                            <Input
                                                                type="number"
                                                                name="MAX_RADIUS_IN_KILOMETERS"
                                                                value={formData.MAX_RADIUS_IN_KILOMETERS || ''}
                                                                onChange={(e) => handleInputChange('MAX_RADIUS_IN_KILOMETERS', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Max Age (Years)</Label>
                                                            <Input
                                                                type="number"
                                                                name="MAX_AGE_IN_YEAR"
                                                                value={formData.MAX_AGE_IN_YEAR || ''}
                                                                onChange={(e) => handleInputChange('MAX_AGE_IN_YEAR', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>

                                                    {/* Date Settings */}
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Buffer Time for Scheduling (Seconds)</Label>
                                                            <Input
                                                                type="number"
                                                                name="BUFFER_TIME_FOR_SCHEDULING_DATE_IN_SECONDS"
                                                                value={formData.BUFFER_TIME_FOR_SCHEDULING_DATE_IN_SECONDS || ''}
                                                                onChange={(e) => handleInputChange('BUFFER_TIME_FOR_SCHEDULING_DATE_IN_SECONDS', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">In-Person Date Duration (Seconds)</Label>
                                                            <Input
                                                                type="number"
                                                                name="DURATION_OF_IN_PERSON_DATE_IN_SECONDS"
                                                                value={formData.DURATION_OF_IN_PERSON_DATE_IN_SECONDS || ''}
                                                                onChange={(e) => handleInputChange('DURATION_OF_IN_PERSON_DATE_IN_SECONDS', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Online Date Duration (Seconds)</Label>
                                                            <Input
                                                                type="number"
                                                                name="DURATION_OF_ONLINE_DATE_IN_SECONDS"
                                                                value={formData.DURATION_OF_ONLINE_DATE_IN_SECONDS || ''}
                                                                onChange={(e) => handleInputChange('DURATION_OF_ONLINE_DATE_IN_SECONDS', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">First Reminder Time (Seconds)</Label>
                                                            <Input
                                                                type="number"
                                                                name="FIRST_REMINDER_OF_UPCOMING_DATE_IN_SECONDS"
                                                                value={formData.FIRST_REMINDER_OF_UPCOMING_DATE_IN_SECONDS || ''}
                                                                onChange={(e) => handleInputChange('FIRST_REMINDER_OF_UPCOMING_DATE_IN_SECONDS', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={4}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Second Reminder Time (Seconds)</Label>
                                                            <Input
                                                                type="number"
                                                                name="SECOND_REMINDER_OF_UPCOMING_DATE_IN_SECONDS"
                                                                value={formData.SECOND_REMINDER_OF_UPCOMING_DATE_IN_SECONDS || ''}
                                                                onChange={(e) => handleInputChange('SECOND_REMINDER_OF_UPCOMING_DATE_IN_SECONDS', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Cooling Off Period (Days)</Label>
                                                            <Input
                                                                type="number"
                                                                name="COOLING_OF_PERIOD_FOR_PASSED_USER"
                                                                value={formData.COOLING_OF_PERIOD_FOR_PASSED_USER || ''}
                                                                onChange={(e) => handleInputChange('COOLING_OF_PERIOD_FOR_PASSED_USER', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Free Video Call Minutes</Label>
                                                            <Input
                                                                type="number"
                                                                name="FREE_MIN_FOR_VIDEO_CALL_MATCH"
                                                                value={formData.FREE_MIN_FOR_VIDEO_CALL_MATCH || ''}
                                                                onChange={(e) => handleInputChange('FREE_MIN_FOR_VIDEO_CALL_MATCH', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    </TabPane>

                                    <TabPane tabId="2">
                                        {appConfig.COMPANY_DETAIL && (
                                            <Card className="border">
                                                <CardHeader>
                                                    <h4 className="card-title mb-0">Company Information</h4>
                                                </CardHeader>
                                                <CardBody>
                                                    <Row className="mb-3">
                                                        <Col lg={6}>
                                                            <div>
                                                                <Label htmlFor="companyName" className="form-label">Company Name</Label>
                                                                <Input 
                                                                    type="text" 
                                                                    className="form-control"
                                                                    name="COMPANY_DETAIL.companyName"
                                                                    value={formData.COMPANY_DETAIL.companyName}
                                                                    onChange={(e) => handleInputChange('COMPANY_DETAIL', { ...formData.COMPANY_DETAIL, companyName: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col lg={6}>
                                                            <div>
                                                                <Label htmlFor="address" className="form-label">Address</Label>
                                                                <Input 
                                                                    type="text" 
                                                                    className="form-control"
                                                                    name="COMPANY_DETAIL.address"
                                                                    value={formData.COMPANY_DETAIL.address}
                                                                    onChange={(e) => handleInputChange('COMPANY_DETAIL', { ...formData.COMPANY_DETAIL, address: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-3">
                                                        <Col lg={3}>
                                                            <div>
                                                                <Label htmlFor="city" className="form-label">City</Label>
                                                                <Input 
                                                                    type="text" 
                                                                    className="form-control"
                                                                    name="COMPANY_DETAIL.city"
                                                                    value={formData.COMPANY_DETAIL.city}
                                                                    onChange={(e) => handleInputChange('COMPANY_DETAIL', { ...formData.COMPANY_DETAIL, city: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col lg={3}>
                                                            <div>
                                                                <Label htmlFor="country" className="form-label">Country</Label>
                                                                <Input 
                                                                    type="text" 
                                                                    className="form-control"
                                                                    name="COMPANY_DETAIL.country"
                                                                    value={formData.COMPANY_DETAIL.country}
                                                                    onChange={(e) => handleInputChange('COMPANY_DETAIL', { ...formData.COMPANY_DETAIL, country: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col lg={3}>
                                                            <div>
                                                                <Label htmlFor="zipCode" className="form-label">Zip Code</Label>
                                                                <Input 
                                                                    type="text" 
                                                                    className="form-control"
                                                                    name="COMPANY_DETAIL.zipCode"
                                                                    value={formData.COMPANY_DETAIL.zipCode}
                                                                    onChange={(e) => handleInputChange('COMPANY_DETAIL', { ...formData.COMPANY_DETAIL, zipCode: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col lg={3}>
                                                            <div>
                                                                <Label htmlFor="taxId" className="form-label">Tax ID</Label>
                                                                <Input 
                                                                    type="text" 
                                                                    className="form-control"
                                                                    name="COMPANY_DETAIL.taxId"
                                                                    value={formData.COMPANY_DETAIL.taxId}
                                                                    onChange={(e) => handleInputChange('COMPANY_DETAIL', { ...formData.COMPANY_DETAIL, taxId: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    {/* New row for Copyright Text and Support Email */}
                                                    <Row>
                                                        <Col lg={6}>
                                                            <div className="mb-3">
                                                                <Label className="form-label">Copyright Text</Label>
                                                                <Input 
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="COPYRIGHT_TEXT"
                                                                    value={formData.COPYRIGHT_TEXT || ''}
                                                                    onChange={(e) => handleInputChange('COPYRIGHT_TEXT', e.target.value)}
                                                                    placeholder="Enter copyright text"
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col lg={6}>
                                                            <div className="mb-3">
                                                                <Label className="form-label">Support Email</Label>
                                                                <Input 
                                                                    type="email"
                                                                    className="form-control"
                                                                    name="SUPPORT_MAIL"
                                                                    value={formData.SUPPORT_MAIL || ''}
                                                                    onChange={(e) => handleInputChange('SUPPORT_MAIL', e.target.value)}
                                                                    placeholder="Enter support email"
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        )}
                                    </TabPane>

                                    <TabPane tabId="3">
                                        {appConfig.BOOST_PROFILE && (
                                            <Card className="border">
                                                <CardHeader>
                                                    <h4 className="card-title mb-0">Boost Profile Configuration</h4>
                                                </CardHeader>
                                                <CardBody>
                                                    <Row className="mb-3">
                                                        <Col lg={3}>
                                                            <div>
                                                                <Label htmlFor="boostMinutes" className="form-label">Duration (Minutes)</Label>
                                                                <Input 
                                                                    type="number" 
                                                                    className="form-control"
                                                                    name="BOOST_PROFILE.minutes"
                                                                    value={formData.BOOST_PROFILE.minutes}
                                                                    onChange={(e) => handleInputChange('BOOST_PROFILE', { ...formData.BOOST_PROFILE, minutes: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col lg={3}>
                                                            <div>
                                                                <Label htmlFor="boostCost" className="form-label">Cost</Label>
                                                                <Input 
                                                                    type="number" 
                                                                    className="form-control"
                                                                    name="BOOST_PROFILE.cost"
                                                                    value={formData.BOOST_PROFILE.cost}
                                                                    onChange={(e) => handleInputChange('BOOST_PROFILE', { ...formData.BOOST_PROFILE, cost: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col lg={3}>
                                                            <div>
                                                                <Label htmlFor="boostRadius" className="form-label">Radius</Label>
                                                                <Input 
                                                                    type="number" 
                                                                    className="form-control"
                                                                    name="BOOST_PROFILE.radius"
                                                                    value={formData.BOOST_PROFILE.radius}
                                                                    onChange={(e) => handleInputChange('BOOST_PROFILE', { ...formData.BOOST_PROFILE, radius: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col lg={3}>
                                                            <div>
                                                                <Label htmlFor="boostRadiusUnit" className="form-label">Radius Unit</Label>
                                                                <Input 
                                                                    type="text" 
                                                                    className="form-control"
                                                                    name="BOOST_PROFILE.radiusUnit"
                                                                    value={formData.BOOST_PROFILE.radiusUnit}
                                                                    onChange={(e) => handleInputChange('BOOST_PROFILE', { ...formData.BOOST_PROFILE, radiusUnit: e.target.value })}
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        )}
                                    </TabPane>

                                    <TabPane tabId="4">
                                        <Card className="border">
                                            <CardHeader>
                                                <h4 className="card-title mb-0">Free Subscription Settings</h4>
                                            </CardHeader>
                                            <CardBody>
                                                {/* Basic Settings */}
                                                <Row className="mb-4">
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Daily Like Count</Label>
                                                            <Input 
                                                                type="number"
                                                                className="form-control mb-2"
                                                                name="FREE_SUBSCRIPTION_PLAN.dailyLikeCount.value"
                                                                value={formData.FREE_SUBSCRIPTION_PLAN.dailyLikeCount.value}
                                                                onChange={(e) => handleInputChange('FREE_SUBSCRIPTION_PLAN', { ...formData.FREE_SUBSCRIPTION_PLAN, dailyLikeCount: { ...formData.FREE_SUBSCRIPTION_PLAN.dailyLikeCount, value: e.target.value } })}
                                                            />
                                                            <Input 
                                                                type="textarea"
                                                                rows="2"
                                                                placeholder="Description"
                                                                name="FREE_SUBSCRIPTION_PLAN.dailyLikeCount.description.en"
                                                                value={formData.FREE_SUBSCRIPTION_PLAN.dailyLikeCount?.description?.en || ''}
                                                                onChange={(e) => handleDescriptionChange('dailyLikeCount', 'description', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Number of Tokens</Label>
                                                            <Input 
                                                                type="number"
                                                                className="form-control mb-2"
                                                                name="FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded.value"
                                                                value={formData.FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded.value}
                                                                onChange={(e) => handleInputChange('FREE_SUBSCRIPTION_PLAN', { ...formData.FREE_SUBSCRIPTION_PLAN, noOfTokensIncluded: { ...formData.FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded, value: e.target.value } })}
                                                            />
                                                            <Input 
                                                                type="textarea"
                                                                rows="2"
                                                                placeholder="Description"
                                                                name="FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded.description.en"
                                                                value={formData.FREE_SUBSCRIPTION_PLAN.noOfTokensIncluded?.description?.en || ''}
                                                                onChange={(e) => handleDescriptionChange('noOfTokensIncluded', 'description', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* My List Settings */}
                                                <Card className="border mb-4">
                                                    <CardHeader>
                                                        <h5 className="card-title mb-0">My List Features</h5>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <Row>
                                                            {/* Recent Visitors */}
                                                            <Col lg={4}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Recent Visitors</Label>
                                                                    <div className="recent-visitors mb-2">
                                                                        <Button 
                                                                            color='success' 
                                                                            className={`custom-toggle ${!formData.FREE_SUBSCRIPTION_PLAN.myList.recentVisitorsValue ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('recentVisitors')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.myList.recentVisitorsDescription.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.myList.recentVisitorsDescription.en}
                                                                        onChange={(e) => handleMyListDescriptionChange('recentVisitors', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* My Matches */}
                                                            <Col lg={4}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">My Matches</Label>
                                                                    <div className="my-matches mb-2">
                                                                        <Button 
                                                                            color='success' 
                                                                            className={`custom-toggle ${!formData.FREE_SUBSCRIPTION_PLAN.myList.myMatchesValue ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('myMatches')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.myList.myMatchesDescription.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.myList.myMatchesDescription.en}
                                                                        onChange={(e) => handleMyListDescriptionChange('myMatches', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* Maybe */}
                                                            <Col lg={4}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Maybe</Label>
                                                                    <div className="maybe mb-2">
                                                                        <Button 
                                                                            color='success' 
                                                                            className={`custom-toggle ${!formData.FREE_SUBSCRIPTION_PLAN.myList.maybeValue ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('maybe')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.myList.maybeValueDescription.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.myList.maybeValueDescription.en}
                                                                        onChange={(e) => handleMyListDescriptionChange('maybe', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* My Likes */}
                                                            <Col lg={4}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">My Likes</Label>
                                                                    <div className="my-likes mb-2">
                                                                        <Button 
                                                                            color='success' 
                                                                            className={`custom-toggle ${!formData.FREE_SUBSCRIPTION_PLAN.myList.myLikesValue ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('myLikes')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.myList.myLikesDescription.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.myList.myLikesDescription.en}
                                                                        onChange={(e) => handleMyListDescriptionChange('myLikes', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* Likes Me */}
                                                            <Col lg={4}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Likes Me</Label>
                                                                    <div className="likes-me mb-2">
                                                                        <Button 
                                                                            color='success' 
                                                                            className={`custom-toggle ${!formData.FREE_SUBSCRIPTION_PLAN.myList.likesMeValue ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('likesMe')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.myList.likesMeDescription.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.myList.likesMeDescription.en}
                                                                        onChange={(e) => handleMyListDescriptionChange('likesMe', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* Liked Hooks */}
                                                            <Col lg={4}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Liked Hooks</Label>
                                                                    <div className="liked-hooks mb-2">
                                                                        <Button 
                                                                            color='success' 
                                                                            className={`custom-toggle ${!formData.FREE_SUBSCRIPTION_PLAN.myList.likedHooksValue ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('likedHooks')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.myList.likedHooksValueDescription.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.myList.likedHooksValueDescription.en}
                                                                        onChange={(e) => handleMyListDescriptionChange('likedHooks', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>

                                                {/* Feature Access */}
                                                <Card className="border">
                                                    <CardHeader>
                                                        <h5 className="card-title mb-0">Feature Access</h5>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <Row>
                                                            {/* Ads Visibility */}
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Ads Visibility</Label>
                                                                    <div className="ads-visibility mb-2">
                                                                        <Button 
                                                                            color='success' 
                                                                            className={`custom-toggle ${formData.FREE_SUBSCRIPTION_PLAN.adsVisibility.value !== 'Visible' ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('adsVisibility')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.adsVisibility.description.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.adsVisibility.description.en}
                                                                        onChange={(e) => handleDescriptionChange('adsVisibility', 'description', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* Allow Unlimited Rewinds */}
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Allow Unlimited Rewinds</Label>
                                                                    <div className="recent-visitors mb-2">
                                                                        <Button 
                                                                            color='success'
                                                                            className={`custom-toggle ${formData.FREE_SUBSCRIPTION_PLAN.allowUnlimitedRewinds.value !== 'Enabled' ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('allowUnlimitedRewinds')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.allowUnlimitedRewinds.description.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.allowUnlimitedRewinds.description.en}
                                                                        onChange={(e) => handleDescriptionChange('allowUnlimitedRewinds', 'description', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* Passport To Another Location */}
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Passport To Another Location</Label>
                                                                    <div className="recent-visitors mb-2">
                                                                        <Button 
                                                                            color='success'
                                                                            className={`custom-toggle ${formData.FREE_SUBSCRIPTION_PLAN.passportToAnotherLocation.value !== 'Enabled' ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('passportToAnotherLocation')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.passportToAnotherLocation.description.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.passportToAnotherLocation.description.en}
                                                                        onChange={(e) => handleDescriptionChange('passportToAnotherLocation', 'description', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* Advance Filter Access */}
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Advance Filter Access</Label>
                                                                    <div className="recent-visitors mb-2">
                                                                        <Button 
                                                                            color='success'
                                                                            className={`custom-toggle ${!formData.FREE_SUBSCRIPTION_PLAN.advanceFilterAccess.value ? 'active' : ''}`}
                                                                            onClick={() => handleToggle('advanceFilterAccess')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.advanceFilterAccess.description.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.advanceFilterAccess.description.en}
                                                                        onChange={(e) => handleDescriptionChange('advanceFilterAccess', 'description', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* Control Who Sees Your Profile */}
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Control Who Sees Your Profile</Label>
                                                                    <div className="recent-visitors mb-2">
                                                                        <Button 
                                                                            color='success'
                                                                            className={`custom-toggle ${formData.FREE_SUBSCRIPTION_PLAN.controlWhoSeesYourProfile.value === true || formData.FREE_SUBSCRIPTION_PLAN.controlWhoSeesYourProfile.value === 'Enabled' ? '' : 'active'}`}
                                                                            onClick={() => handleToggle('controlWhoSeesYourProfile')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.controlWhoSeesYourProfile.description.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.controlWhoSeesYourProfile.description.en}
                                                                        onChange={(e) => handleDescriptionChange('controlWhoSeesYourProfile', 'description', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>

                                                            {/* AI Matches */}
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">AI Matches</Label>
                                                                    <div className="recent-visitors mb-2">
                                                                        <Button 
                                                                            color='success'
                                                                            className={`custom-toggle ${formData.FREE_SUBSCRIPTION_PLAN.aiMatches.value === true || formData.FREE_SUBSCRIPTION_PLAN.aiMatches.value === 'Enabled' ? '' : 'active'}`}
                                                                            onClick={() => handleToggle('aiMatches')}
                                                                        >
                                                                            <span className="icon-off">
                                                                                <i className="ri-thumb-down-line align-bottom me-1"></i> No
                                                                            </span>
                                                                            <span className="icon-on">
                                                                                <i className="ri-thumb-up-line align-bottom me-1"></i> Yes
                                                                            </span>
                                                                        </Button>
                                                                    </div>
                                                                    <Input 
                                                                        type="textarea"
                                                                        rows="2"
                                                                        placeholder="Description"
                                                                        name="FREE_SUBSCRIPTION_PLAN.aiMatches.description.en"
                                                                        value={formData.FREE_SUBSCRIPTION_PLAN.aiMatches.description.en}
                                                                        onChange={(e) => handleDescriptionChange('aiMatches', 'description', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>
                                            </CardBody>
                                        </Card>
                                    </TabPane>

                                    <TabPane tabId="5">
                                        <Card className="border">
                                            <CardHeader>
                                                <h4 className="card-title mb-0">Chat and Call Settings</h4>
                                            </CardHeader>
                                            <CardBody>
                                                {/* Token Settings */}
                                                <Row className="mb-4">
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Tokens for Paid Chat Message</Label>
                                                            <Input 
                                                                type="number"
                                                                className="form-control"
                                                                name="NO_OF_COIN_FOR_PAID_CHAT_MSG"
                                                                value={formData.NO_OF_COIN_FOR_PAID_CHAT_MSG}
                                                                onChange={(e) => handleInputChange('NO_OF_COIN_FOR_PAID_CHAT_MSG', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="mb-3">
                                                            <Label className="form-label">Tokens for Super Like</Label>
                                                            <Input 
                                                                type="number"
                                                                className="form-control"
                                                                name="TOKEN_FOR_SUPER_LIKE"
                                                                value={formData.TOKEN_FOR_SUPER_LIKE}
                                                                onChange={(e) => handleInputChange('TOKEN_FOR_SUPER_LIKE', e.target.value)}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>

                                                {/* Video Call Extension Plans */}
                                                <Card className="border">
                                                    <CardHeader className="bg-light d-flex justify-content-between align-items-center">
                                                        <h5 className="card-title mb-0">Video Call Extension Plans</h5>
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={() => {
                                                                const newPlans = [...formData.EXTENSION_PLAN_FOR_VIDEO_CALL, { min: 0, tokens: 0 }];
                                                                handleInputChange('EXTENSION_PLAN_FOR_VIDEO_CALL', newPlans);
                                                            }}
                                                        >
                                                            <i className="ri-add-line align-bottom"></i> Add Plan
                                                        </Button>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <Row>
                                                            {formData.EXTENSION_PLAN_FOR_VIDEO_CALL.map((plan, index) => (
                                                                <Col lg={6} key={index}>
                                                                    <div className="mb-3">
                                                                        <Label className="form-label">Plan {index + 1}</Label>
                                                                        <div className="d-flex gap-3">
                                                                            <div className="flex-grow-1">
                                                                                <Label className="form-label small">Duration (minutes)</Label>
                                                                                <Input 
                                                                                    type="number"
                                                                                    className="form-control"
                                                                                    name="EXTENSION_PLAN_FOR_VIDEO_CALL.min"
                                                                                    value={plan.min}
                                                                                    onChange={(e) => {
                                                                                        const newPlans = [...formData.EXTENSION_PLAN_FOR_VIDEO_CALL];
                                                                                        newPlans[index] = {
                                                                                            ...newPlans[index],
                                                                                            min: parseInt(e.target.value)
                                                                                        };
                                                                                        handleInputChange('EXTENSION_PLAN_FOR_VIDEO_CALL', newPlans);
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                            <div className="flex-grow-1">
                                                                                <Label className="form-label small">Tokens</Label>
                                                                                <Input 
                                                                                    type="number"
                                                                                    className="form-control"
                                                                                    name="EXTENSION_PLAN_FOR_VIDEO_CALL.tokens"
                                                                                    value={plan.tokens}
                                                                                    onChange={(e) => {
                                                                                        const newPlans = [...formData.EXTENSION_PLAN_FOR_VIDEO_CALL];
                                                                                        newPlans[index] = {
                                                                                            ...newPlans[index],
                                                                                            tokens: parseInt(e.target.value)
                                                                                        };
                                                                                        handleInputChange('EXTENSION_PLAN_FOR_VIDEO_CALL', newPlans);
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                            {formData.EXTENSION_PLAN_FOR_VIDEO_CALL.length > 1 && (
                                                                                <Button
                                                                                    color="danger"
                                                                                    className="align-self-end"
                                                                                    onClick={() => {
                                                                                        const newPlans = formData.EXTENSION_PLAN_FOR_VIDEO_CALL.filter((_, i) => i !== index);
                                                                                        handleInputChange('EXTENSION_PLAN_FOR_VIDEO_CALL', newPlans);
                                                                                    }}
                                                                                >
                                                                                    <i className="ri-delete-bin-line align-bottom"></i>
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    </CardBody>
                                                </Card>
                                            </CardBody>
                                        </Card>
                                    </TabPane>

                                    <TabPane tabId="6">
                                        <Card className="border">
                                            <CardHeader>
                                                <h4 className="card-title mb-0">AI Configuration</h4>
                                            </CardHeader>
                                            <CardBody>
                                                {/* Token Settings */}
                                                <Card className="border mb-4">
                                                    <CardHeader className="bg-light">
                                                        <h5 className="card-title mb-0">Token Settings</h5>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <Row>
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Tokens for Ice Breaker</Label>
                                                                    <Input 
                                                                        type="number"
                                                                        className="form-control"
                                                                        name="NO_OF_COIN_FOR_ICE_BREAKER"
                                                                        value={formData.NO_OF_COIN_FOR_ICE_BREAKER}
                                                                        onChange={(e) => handleInputChange('NO_OF_COIN_FOR_ICE_BREAKER', e.target.value)}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col lg={6}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Face Verification Token</Label>
                                                                    <div className="d-flex gap-2">
                                                                        <Input 
                                                                            type="text"
                                                                            className="form-control font-monospace"
                                                                            name="FACE_VERIFICATION_TOKEN"
                                                                            value={formData.FACE_VERIFICATION_TOKEN}
                                                                            onChange={(e) => handleInputChange('FACE_VERIFICATION_TOKEN', e.target.value)}
                                                                        />
                                                                        <Button 
                                                                            color="light" 
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(formData.FACE_VERIFICATION_TOKEN);
                                                                                // You might want to add a toast notification here
                                                                            }}
                                                                            title="Copy to clipboard"
                                                                        >
                                                                            <i className="ri-file-copy-line"></i>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>

                                                {/* API Configuration */}
                                                <Card className="border">
                                                    <CardHeader className="bg-light">
                                                        <h5 className="card-title mb-0">API Configuration</h5>
                                                    </CardHeader>
                                                    <CardBody>
                                                        <Row>
                                                            <Col lg={12}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">AI Model API Key</Label>
                                                                    <div className="d-flex gap-2">
                                                                        <Input 
                                                                            type="text"
                                                                            className="form-control font-monospace"
                                                                            name="AI_MODEL_API_KEY"
                                                                            value={formData.AI_MODEL_API_KEY}
                                                                            onChange={(e) => handleInputChange('AI_MODEL_API_KEY', e.target.value)}
                                                                        />
                                                                        <Button 
                                                                            color="light" 
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(formData.AI_MODEL_API_KEY);
                                                                                // You might want to add a toast notification here
                                                                            }}
                                                                            title="Copy to clipboard"
                                                                        >
                                                                            <i className="ri-file-copy-line"></i>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            <Col lg={12}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Ice Break URL</Label>
                                                                    <div className="d-flex gap-2">
                                                                        <Input 
                                                                            type="url"
                                                                            className="form-control font-monospace"
                                                                            name="AI_ICE_BREAK_URL"
                                                                            value={formData.AI_ICE_BREAK_URL}
                                                                            onChange={(e) => handleInputChange('AI_ICE_BREAK_URL', e.target.value)}
                                                                        />
                                                                        <Button 
                                                                            color="light" 
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(formData.AI_ICE_BREAK_URL);
                                                                            }}
                                                                            title="Copy to clipboard"
                                                                        >
                                                                            <i className="ri-file-copy-line"></i>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            <Col lg={12}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Recommendation URL</Label>
                                                                    <div className="d-flex gap-2">
                                                                        <Input 
                                                                            type="url"
                                                                            className="form-control font-monospace"
                                                                            name="AI_RECOMMENDATION_URL"
                                                                            value={formData.AI_RECOMMENDATION_URL}
                                                                            onChange={(e) => handleInputChange('AI_RECOMMENDATION_URL', e.target.value)}
                                                                        />
                                                                        <Button 
                                                                            color="light" 
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(formData.AI_RECOMMENDATION_URL);
                                                                            }}
                                                                            title="Copy to clipboard"
                                                                        >
                                                                            <i className="ri-file-copy-line"></i>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            <Col lg={12}>
                                                                <div className="mb-3">
                                                                    <Label className="form-label">Star URL</Label>
                                                                    <div className="d-flex gap-2">
                                                                        <Input 
                                                                            type="url"
                                                                            className="form-control font-monospace"
                                                                            name="AI_STAR_URL"
                                                                            value={formData.AI_STAR_URL}
                                                                            onChange={(e) => handleInputChange('AI_STAR_URL', e.target.value)}
                                                                        />
                                                                        <Button 
                                                                            color="light" 
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(formData.AI_STAR_URL);
                                                                            }}
                                                                            title="Copy to clipboard"
                                                                        >
                                                                            <i className="ri-file-copy-line"></i>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>
                                            </CardBody>
                                        </Card>
                                    </TabPane>
                                </TabContent>

                                {isEdited && <SaveButton />}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Settings;